import { create } from 'zustand';
import { persist } from 'zustand/middleware';

// ✅ Define the StorageValue type used by zustand persist
type StorageValue<T> = {
  state: T;
  version: number;
};

export interface Message {
  id: string;
  content: string;
  type: 'user' | 'ai';
  timestamp: string; // Stored as ISO string
}

interface ChatState {
  messages: Message[];
  isOpen: boolean;
  isTyping: boolean;
  suggestions: string[];
  settings: {
    theme: 'light' | 'dark' | 'system';
    position: 'bottom-right' | 'bottom-left';
    showAvatar: boolean;
    showTimestamp: boolean;
    enableMarkdown: boolean;
  };
  addMessage: (message: Omit<Message, 'id' | 'timestamp'>) => void;
  clearMessages: () => void;
  setIsOpen: (isOpen: boolean) => void;
  setIsTyping: (isTyping: boolean) => void;
  setSuggestions: (suggestions: string[]) => void;
  updateSettings: (settings: Partial<ChatState['settings']>) => void;
}

// ✅ Safe JSON parse utility
function safeParseJSON<T>(value: string | null): T | null {
  try {
    if (!value) return null;
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

// ✅ Utility to handle cookies
function getCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
  return match ? match[2] : null;
}

function setCookie(name: string, value: string, days: number): void {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
  document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; Secure; HttpOnly`;
}

function removeCookie(name: string): void {
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; Secure; HttpOnly`;
}

// ✅ Zustand store with persistence
export const useChatStore = create<ChatState>()(
  persist(
    (set) => ({
      messages: [],
      isOpen: false,
      isTyping: false,
      suggestions: [
        'How can I create a budget?',
        'What are good investment options for beginners?',
        'Tips for saving money?',
        'How to reduce expenses?'
      ],
      settings: {
        theme: 'system',
        position: 'bottom-right',
        showAvatar: true,
        showTimestamp: true,
        enableMarkdown: true
      },
      addMessage: (message) =>
        set((state) => ({
          messages: [
            ...state.messages,
            {
              ...message,
              id: Date.now().toString(),
              timestamp: new Date().toISOString()
            }
          ]
        })),
      clearMessages: () => set({ messages: [] }),
      setIsOpen: (isOpen) => set({ isOpen }),
      setIsTyping: (isTyping) => set({ isTyping }),
      setSuggestions: (suggestions) => set({ suggestions }),
      updateSettings: (newSettings) =>
        set((state) => ({
          settings: {
            ...state.settings,
            ...newSettings
          }
        }))
    }),
    {
      name: 'chat-store',
      storage: {
        getItem: (name): StorageValue<ChatState> | null => {
          const cookieValue = getCookie(name);
          const parsed = safeParseJSON<StorageValue<ChatState>>(cookieValue);
          if (parsed === null) {
            removeCookie(name);
            return null;
          }
          return parsed;
        },
        setItem: (name, value) => setCookie(name, JSON.stringify(value), 7),  // Expiry in 7 days
        removeItem: (name) => removeCookie(name)
      },
      partialize: (state) => state
    }
  )
);
